import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type FormManager from '@/services/forms/baseForm/formManager'
import type { Forms } from '@/services/forms/formTypes'
import { TmFormError } from '@/core/error/tmFormError'
import type AttachmentFileService from '@/services/domain/attachmentFile/attachmentFileService'
import type { AttachedFileWithRelatedId } from '@/components/domain/attachments/types'

@injectable()
export default class AttachmentsFormHelper {
  constructor(
    @inject(SERVICE_TYPES.FormManager) protected readonly formManager: FormManager,
    @inject(SERVICE_TYPES.AttachmentFileService) protected readonly attachmentFileService: AttachmentFileService,
  ) {}

  public getAttachedFiles(formId: Forms, fieldName: string): AttachedFileWithRelatedId[] {
    const formService = this.getForm(formId)

    return this.getFileIdsArrayField(formId, fieldName).field.children.reduce((result, fieldId) => {
      const fileId = formService.getFieldValueSafe<string>(formService.getNameById(fieldId))
      const file = this.attachmentFileService.findEntityByIdOrNull(fileId)

      if (file) {
        result.push({
          relatedId: fieldId,
          file,
        })
      }

      return result
    }, [] as AttachedFileWithRelatedId[])
  }

  public removeFileId(formId: Forms, fileFieldId: string) {
    this.getForm(formId).removeFieldById(fileFieldId)
  }

  public addFileId(formId: Forms, fieldName: string, fileId: string) {
    const formService = this.getForm(formId)
    const arrayField = this.getFileIdsArrayField(formId, fieldName)
    const field = formService.extendArray(arrayField.field.name)
    formService.setFieldValueById(field.field.id, fileId)
  }

  protected getFileIdsArrayField(formId: Forms, fieldName: string) {
    const formService = this.getForm(formId)
    const field = formService.getField(fieldName)

    if (!formService.isArrayField(field)) {
      throw new TmFormError(`"${formId}.${fieldName}" field is not an array`)
    }

    return field
  }

  protected getForm(formId: Forms) {
    return this.formManager.getForm(formId)
  }
}
